import { useState, useEffect, useCallback, useRef } from "react";
import { Download } from "lucide-react";
import {
  adminGetAllBusiness,
  adminGetUsers,
  adminGetUserHistory,
  adminUpdateUser,
  adminChangeUserEmail,
  adminDeleteMembership,
  adminDeleteUser,
} from "../../../../api/apiEndpoints";
import { formatCurrency } from "../../../../utils/partnerMetrics";
import { downloadCsv } from "../../../../utils/reportDates";
import MemberMembership from "../MemberMembership";
import MultiSelect from "../MultiSelect";
import {
  SectionTitle, Card, Row, FilterRow, Input, Select, Button, Table, Th, Td, Tr, Badge, FormGrid, Label, FormField, StatusText, ModalOverlay, Modal, ModalHeader, ModalTitle, CloseButton, EmptyState, StatCards, StatCard, StatValue, StatLabel,
} from "../styles";

const USERS_PER_PAGE = 50;

const PREMIUM_PRODUCT_NAMES = ["premium", "premium_quarterly", "premium_yearly"];

const isoDate = (v?: string | null) => (v ? new Date(v).toISOString().slice(0, 10) : "");

const UsersPage = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [gymFilter, setGymFilter] = useState<string[]>([]);
  const [cityFilter, setCityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [tierFilter, setTierFilter] = useState("");
  const [checkInFilter, setCheckInFilter] = useState("");
  const [nameSort, setNameSort] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [detailsForm, setDetailsForm] = useState({ firstName: "", lastName: "", phone: "" });
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsStatus, setDetailsStatus] = useState<{ msg: string; ok: boolean } | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<{ msg: string; ok: boolean } | null>(null);
  const [deleteMembershipLoading, setDeleteMembershipLoading] = useState<string | null>(null);
  const [deleteForm, setDeleteForm] = useState({ reason: "", confirmEmail: "" });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState<{ msg: string; ok: boolean } | null>(null);

  useEffect(() => {
    adminGetAllBusiness()
      .then((res) => setBusinesses((res.data as any).businesses ?? []))
      .catch(() => setBusinesses([]));
  }, []);

  const metroById: Record<string, string> = {};
  const metroByName: Record<string, string> = {};
  businesses.forEach((b: any) => {
    metroById[b.businessId] = b.metro;
    if (b.name) metroByName[b.name] = b.metro;
  });
  const metroOf = (u: any) => metroById[u.currentBusinessId] ?? metroByName[u.currentGym];

  const cities = Array.from(new Set(businesses.map((b: any) => b.metro).filter(Boolean))).sort() as string[];

  const gymNames = Array.from(
    new Set(
      businesses
        .filter((b: any) => !cityFilter || b.metro === cityFilter)
        .map((b: any) => b.name)
        .filter(Boolean)
    )
  ).sort() as string[];

  const requestRef = useRef(0);

  const fetchUsers = useCallback(async (s: string): Promise<any[] | null> => {
    const id = ++requestRef.current;
    setLoading(true);
    try {
      const res = await adminGetUsers({ search: s || undefined });
      if (id !== requestRef.current) return null;
      setUsers(res.data);
      return res.data;
    } catch {
      if (id === requestRef.current) setUsers([]);
      return null;
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  }, []);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchUsers(search);
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, fetchUsers]);

  useEffect(() => {
    setPage(1);
  }, [search, cityFilter, gymFilter, tierFilter, statusFilter, checkInFilter, nameSort]);

  const openUser = async (user: any) => {
    setSelectedUser(user);
    setDeleteForm({ reason: "", confirmEmail: "" });
    setDeleteStatus(null);
    setDetailsForm({ firstName: user.firstName ?? "", lastName: user.lastName ?? "", phone: user.phone ?? "" });
    setDetailsStatus(null);
    setNewEmail("");
    setEmailStatus(null);
    setHistoryLoading(true);
    try {
      const res = await adminGetUserHistory(user.userId);
      setHistory(res.data);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!selectedUser) return;
    setDetailsLoading(true);
    setDetailsStatus(null);
    try {
      await adminUpdateUser(selectedUser.userId, {
        firstName: detailsForm.firstName.trim(),
        lastName: detailsForm.lastName.trim(),
        phone: detailsForm.phone.trim(),
      });
      setDetailsStatus({ msg: "Details saved.", ok: true });
      setSelectedUser({ ...selectedUser, ...detailsForm });
      fetchUsers(search);
    } catch (e: any) {
      setDetailsStatus({ msg: e?.response?.data?.message || "Failed to save details.", ok: false });
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!selectedUser || !newEmail.trim()) return;
    if (!window.confirm(
      `Change email to "${newEmail.trim()}"?\n\nThis updates Firebase login, Stripe billing, and our database. The member will need to sign in again with the new email.`
    )) return;
    setEmailLoading(true);
    setEmailStatus(null);
    try {
      await adminChangeUserEmail(selectedUser.userId, newEmail.trim());
      setEmailStatus({ msg: `Email changed to ${newEmail.trim()}.`, ok: true });
      setSelectedUser({ ...selectedUser, email: newEmail.trim() });
      setNewEmail("");
      fetchUsers(search);
    } catch (e: any) {
      setEmailStatus({ msg: e?.response?.data?.message || "Failed to change email.", ok: false });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleDeleteMembership = async (h: any) => {
    if (!window.confirm(
      `Delete this ${h.gymName || "membership"} row?\n\nThis revokes the member's access and cancels the Stripe subscription. Use after a refund. This cannot be undone.`
    )) return;
    setDeleteMembershipLoading(h.membershipId);
    try {
      await adminDeleteMembership(h.membershipId);
      if (selectedUser) {
        const res = await adminGetUserHistory(selectedUser.userId);
        setHistory(res.data);
      }
      fetchUsers(search);
    } catch (e: any) {
      alert(e?.response?.data?.message || "Failed to delete membership.");
    } finally {
      setDeleteMembershipLoading(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Permanently delete ${selectedUser.email}? This can't be undone.`)) return;
    setDeleteLoading(true);
    setDeleteStatus(null);
    try {
      await adminDeleteUser(selectedUser.userId, {
        reason: deleteForm.reason.trim(),
        confirmEmail: deleteForm.confirmEmail.trim(),
      });
      setSelectedUser(null);
      fetchUsers(search);
    } catch (e: any) {
      setDeleteStatus({ msg: e?.response?.data?.error || "Failed to delete the account.", ok: false });
    } finally {
      setDeleteLoading(false);
    }
  };

  const refreshSelected = async () => {
    if (!selectedUser) return;
    const [fresh, hist] = await Promise.all([
      fetchUsers(search),
      adminGetUserHistory(selectedUser.userId).catch(() => null),
    ]);
    const updated = fresh?.find((u) => u.userId === selectedUser.userId);
    if (updated) setSelectedUser(updated);
    if (hist) setHistory(hist.data);
  };

  const now = new Date();

  // "active"              → membership valid, no scheduled cancel
  // "scheduled_to_cancel" → membership valid, cancel is queued
  // "no_membership"       → no valid membership
  const getStatus = (u: any) => {
    const hasActiveMembership = u.membershipId && u.expiresAt && new Date(u.expiresAt) > now;
    const pauseUpcoming = u.pausedUntil && new Date(u.pausedUntil) > now;
    if (pauseUpcoming) return hasActiveMembership ? "pause_scheduled" : "paused";
    if (!hasActiveMembership) return "no_membership";
    if (u.scheduledCancelAt && new Date(u.scheduledCancelAt) > now) return "scheduled_to_cancel";
    return "active";
  };

  const isActiveAny = (u: any) => {
    const s = getStatus(u);
    return s === "active" || s === "scheduled_to_cancel" || s === "pause_scheduled";
  };

  // Use membershipProductName (from actual membership row) — more accurate than member_settings
  const getTier = (u: any) => {
    const name = (u.membershipProductName || u.currentProductName || "").toLowerCase();
    if (PREMIUM_PRODUCT_NAMES.includes(name)) return "premium";
    if (name === "basic") return "basic";
    return null;
  };

  const getCheckIn = (u: any) =>
    !u.membershipId || u.isOnboarded === undefined ? null : u.isOnboarded ? "checked_in" : "not_checked_in";

  // statsBase: filtered by city/gym only — drives the stat cards so they stay meaningful
  // regardless of tier/status filter selections
  const statsBase = users.filter((u) => {
    if (gymFilter.length && !gymFilter.includes(u.currentGym)) return false;
    if (cityFilter && metroOf(u) !== cityFilter) return false;
    return true;
  });

  const filteredUsers = statsBase.filter((u) => {
    // "active" filter = any user with a valid membership (including scheduled-to-cancel)
    if (statusFilter === "active" && !isActiveAny(u)) return false;
    if (statusFilter === "active_only" && getStatus(u) !== "active") return false;
    if (statusFilter === "scheduled_to_cancel" && getStatus(u) !== "scheduled_to_cancel") return false;
    if (statusFilter === "no_membership" && getStatus(u) !== "no_membership") return false;
    if (statusFilter === "paused" && getStatus(u) !== "paused") return false;
    if (statusFilter === "pause_scheduled" && getStatus(u) !== "pause_scheduled") return false;
    if (tierFilter === "premium" && getTier(u) !== "premium") return false;
    if (tierFilter === "basic" && getTier(u) !== "basic") return false;
    if (checkInFilter && getCheckIn(u) !== checkInFilter) return false;
    return true;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aName = `${a.firstName ?? ""} ${a.lastName ?? ""}`.toLowerCase();
    const bName = `${b.firstName ?? ""} ${b.lastName ?? ""}`.toLowerCase();
    if (aName < bName) return nameSort === "asc" ? -1 : 1;
    if (aName > bName) return nameSort === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / USERS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = sortedUsers.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);

  const activeCount = statsBase.filter(isActiveAny).length;
  const cancelledCount = statsBase.filter((u) => getStatus(u) === "scheduled_to_cancel").length;
  const noMembershipCount = statsBase.filter((u) => getStatus(u) === "no_membership").length;
  const pausedCount = statsBase.filter((u) => getStatus(u) === "paused").length;
  const pauseScheduledCount = statsBase.filter((u) => getStatus(u) === "pause_scheduled").length;
  // Premium/Basic only among active members — should sum to activeCount (minus unknown tiers)
  const premiumCount = statsBase.filter((u) => isActiveAny(u) && getTier(u) === "premium").length;
  const basicCount = statsBase.filter((u) => isActiveAny(u) && getTier(u) === "basic").length;

  const statusLabel = (u: any) =>
    ({
      active: "Active",
      scheduled_to_cancel: "Cancelling",
      pause_scheduled: "Pause scheduled",
      paused: "Paused",
      no_membership: "No membership",
    } as Record<string, string>)[getStatus(u)];

  const downloadUsers = () =>
    downloadCsv(
      [
        [
          "First name", "Last name", "Email", "Phone", "City", "Current gym", "Next gym", "Tier", "Plan",
          "Membership started", "Expires", "Status", "Cancels on", "Checked in", "Checked in on",
        ],
        ...sortedUsers.map((u) => [
          u.firstName, u.lastName, u.email, u.phone, metroOf(u) ?? "", u.currentGym, u.nextGym,
          getTier(u) ?? "", u.membershipProductName ?? "", isoDate(u.startedAt), isoDate(u.expiresAt),
          statusLabel(u), isoDate(u.scheduledCancelAt),
          getCheckIn(u) === "checked_in" ? "Yes" : getCheckIn(u) === "not_checked_in" ? "No" : "",
          isoDate(u.onboardedAt),
        ]),
      ],
      `Users ${new Date().toISOString().slice(0, 10)}.csv`
    );

  return (
    <>
      <StatCards>
        <StatCard><StatValue>{statsBase.length}</StatValue><StatLabel>Total Accounts</StatLabel></StatCard>
        <StatCard><StatValue>{activeCount}</StatValue><StatLabel>Active Members</StatLabel></StatCard>
        <StatCard><StatValue>{premiumCount}</StatValue><StatLabel>Premium</StatLabel></StatCard>
        <StatCard><StatValue>{basicCount}</StatValue><StatLabel>Basic</StatLabel></StatCard>
        <StatCard><StatValue>{cancelledCount}</StatValue><StatLabel>Cancelling</StatLabel></StatCard>
        <StatCard>
          <StatValue>{pausedCount}</StatValue>
          <StatLabel>Paused{pauseScheduledCount ? ` · ${pauseScheduledCount} scheduled` : ""}</StatLabel>
        </StatCard>
        <StatCard><StatValue>{noMembershipCount}</StatValue><StatLabel>No Membership</StatLabel></StatCard>
      </StatCards>

      <Card>
        <FilterRow>
          <Input placeholder="Search name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select
            value={cityFilter}
            onChange={(e) => {
              const city = e.target.value;
              setCityFilter(city);
              setGymFilter((g) => (city ? g.filter((n) => metroByName[n] === city) : g));
            }}
          >
            <option value="">All cities</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
          <MultiSelect options={gymNames} selected={gymFilter} onChange={setGymFilter} allLabel="All gyms" noun="gyms" />
          <Select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
            <option value="">All tiers</option>
            <option value="basic">Basic</option>
            <option value="premium">Premium</option>
          </Select>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="active">Active (incl. cancelling)</option>
            <option value="active_only">Active – not cancelling</option>
            <option value="scheduled_to_cancel">Scheduled to cancel</option>
            <option value="pause_scheduled">Pause scheduled</option>
            <option value="paused">Paused</option>
            <option value="no_membership">No membership</option>
          </Select>
          <Select value={checkInFilter} onChange={(e) => setCheckInFilter(e.target.value)}>
            <option value="">Checked in: any</option>
            <option value="checked_in">Checked in</option>
            <option value="not_checked_in">Not checked in</option>
          </Select>
        </FilterRow>

        <Row style={{ justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "#555" }}>
            {loading ? "Loading…" : `${sortedUsers.length.toLocaleString()} users match these filters`}
          </span>
          <Button variant="secondary" onClick={downloadUsers} disabled={loading || sortedUsers.length === 0}>
            <Download size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            Download CSV
          </Button>
        </Row>

        {loading ? (
          <EmptyState>Loading…</EmptyState>
        ) : sortedUsers.length === 0 ? (
          <EmptyState>No users found.</EmptyState>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table>
              <thead>
                <tr>
                  <Th
                    onClick={() => setNameSort((d) => (d === "asc" ? "desc" : "asc"))}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    Name {nameSort === "asc" ? "A-Z" : "Z-A"}
                  </Th>
                  <Th>Email</Th>
                  <Th>Current Gym</Th>
                  <Th>Next Gym</Th>
                  <Th>Tier</Th>
                  <Th>Expires</Th>
                  <Th>Status</Th>
                  <Th>Checked in</Th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.map((u) => (
                  <Tr key={u.userId} onClick={() => openUser(u)}>
                    <Td>{u.firstName} {u.lastName}</Td>
                    <Td data-label="Email" style={{ overflowWrap: "anywhere" }}>{u.email}</Td>
                    <Td data-label="Current gym">{u.currentGym || "—"}</Td>
                    <Td data-label="Next gym">{u.nextGym || "—"}</Td>
                    <Td data-label="Tier">
                      {getTier(u) === "premium"
                        ? <Badge color="#fef3c7">Premium</Badge>
                        : getTier(u) === "basic"
                        ? <Badge color="#dbeafe">Basic</Badge>
                        : <Badge color="#f3f4f6">—</Badge>}
                    </Td>
                    <Td data-label="Expires">{u.expiresAt ? new Date(u.expiresAt).toLocaleDateString() : "—"}</Td>
                    <Td data-label="Status">
                      {getStatus(u) === "scheduled_to_cancel"
                        ? <Badge color="#fee2e2">Cancelling</Badge>
                        : getStatus(u) === "paused"
                        ? <Badge color="#e0e7ff" title={`Resumes ${new Date(u.pausedUntil).toLocaleDateString()}`}>Paused</Badge>
                        : getStatus(u) === "pause_scheduled"
                        ? <Badge color="#ede9fe" title={`Pause starts at renewal, resumes ${new Date(u.pausedUntil).toLocaleDateString()}`}>Pause scheduled</Badge>
                        : getStatus(u) === "active"
                        ? <Badge color="#dcfce7">Active</Badge>
                        : <Badge color="#f3f4f6">None</Badge>}
                    </Td>
                    <Td data-label="Checked in">
                      {getCheckIn(u) === "checked_in"
                        ? <Badge color="#dcfce7" title={u.onboardedAt ? `Checked in ${new Date(u.onboardedAt).toLocaleDateString()}` : undefined}>Yes</Badge>
                        : getCheckIn(u) === "not_checked_in"
                        ? <Badge color="#fef3c7">Not yet</Badge>
                        : "—"}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {!loading && totalPages > 1 && (
          <Row style={{ marginTop: 16, marginBottom: 0, justifyContent: "center", alignItems: "center" }}>
            <Button variant="secondary" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>← Prev</Button>
            <span style={{ fontSize: 13, color: "#555" }}>
              Page {currentPage} of {totalPages} · {sortedUsers.length.toLocaleString()} users
            </span>
            <Button variant="secondary" onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages}>Next →</Button>
          </Row>
        )}
      </Card>

      {selectedUser && (
        <ModalOverlay onClick={() => setSelectedUser(null)}>
          <Modal onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680 }}>
            <ModalHeader>
              <ModalTitle>{selectedUser.firstName} {selectedUser.lastName}</ModalTitle>
              <CloseButton onClick={() => setSelectedUser(null)}>×</CloseButton>
            </ModalHeader>

            <p style={{ margin: "0 0 4px", fontSize: 13, color: "#555" }}>{selectedUser.email}</p>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#555" }}>
              Current gym: <strong>{selectedUser.currentGym || "None"}</strong>
              {selectedUser.scheduledCancelAt && new Date(selectedUser.scheduledCancelAt) > now && (
                <> · <span style={{ color: "#dc2626" }}>Cancels {new Date(selectedUser.scheduledCancelAt).toLocaleDateString()}</span></>
              )}
            </p>

            <MemberMembership
              key={selectedUser.userId}
              userId={selectedUser.userId}
              businesses={businesses}
              onChanged={refreshSelected}
            />

            <SectionTitle style={{ fontSize: 14, marginTop: 24, marginBottom: 12 }}>Edit details</SectionTitle>
            <FormGrid>
              <FormField>
                <Label>First name</Label>
                <Input value={detailsForm.firstName} onChange={(e) => setDetailsForm({ ...detailsForm, firstName: e.target.value })} />
              </FormField>
              <FormField>
                <Label>Last name</Label>
                <Input value={detailsForm.lastName} onChange={(e) => setDetailsForm({ ...detailsForm, lastName: e.target.value })} />
              </FormField>
              <FormField>
                <Label>Phone</Label>
                <Input value={detailsForm.phone} onChange={(e) => setDetailsForm({ ...detailsForm, phone: e.target.value })} />
              </FormField>
            </FormGrid>
            <Row style={{ marginTop: 12 }}>
              <Button onClick={handleSaveDetails} disabled={detailsLoading}>{detailsLoading ? "Saving…" : "Save details"}</Button>
            </Row>
            {detailsStatus && <StatusText success={detailsStatus.ok}>{detailsStatus.msg}</StatusText>}

            <SectionTitle style={{ fontSize: 14, marginTop: 24, marginBottom: 12 }}>Change email</SectionTitle>
            <Row style={{ marginBottom: 6 }}>
              <Input type="email" placeholder="new-email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} style={{ flex: 1 }} />
              <Button variant="secondary" onClick={handleChangeEmail} disabled={!newEmail.trim() || emailLoading}>
                {emailLoading ? "Changing…" : "Change email"}
              </Button>
            </Row>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
              Updates Firebase login, Stripe billing, and the database. The member signs in again with the new email.
            </div>
            {emailStatus && <StatusText success={emailStatus.ok}>{emailStatus.msg}</StatusText>}

            <SectionTitle style={{ fontSize: 14, marginTop: 24, marginBottom: 12 }}>Membership History</SectionTitle>
            {historyLoading ? (
              <EmptyState>Loading…</EmptyState>
            ) : history.length === 0 ? (
              <EmptyState>No history found.</EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Gym</Th>
                    <Th>Product</Th>
                    <Th>Started</Th>
                    <Th>Expires</Th>
                    <Th>Paid</Th>
                    <Th>Checked in</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h: any) => (
                    <tr key={h.membershipId}>
                      <Td>{h.gymName}</Td>
                      <Td data-label="Product">{h.productName}</Td>
                      <Td data-label="Started">{h.startedAt ? new Date(h.startedAt).toLocaleDateString() : "—"}</Td>
                      <Td data-label="Expires">{h.expiresAt ? new Date(h.expiresAt).toLocaleDateString() : "—"}</Td>
                      <Td data-label="Paid">{formatCurrency(Number(h.totalPaid ?? 0))}</Td>
                      <Td data-label="Checked in">{h.isOnboarded ? (h.onboardedAt ? new Date(h.onboardedAt).toLocaleDateString() : "Yes") : "No"}</Td>
                      <Td>
                        <Button
                          variant="danger"
                          onClick={() => handleDeleteMembership(h)}
                          disabled={deleteMembershipLoading === h.membershipId}
                          style={{ padding: "4px 10px", fontSize: 12 }}
                        >
                          {deleteMembershipLoading === h.membershipId ? "…" : "Delete"}
                        </Button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            <SectionTitle style={{ fontSize: 14, marginTop: 32, marginBottom: 8, color: "#b91c1c" }}>Delete account</SectionTitle>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 12, lineHeight: 1.6 }}>
              Same as deleting from the app: cancels all Stripe billing, cancels upcoming class bookings, removes their
              login and personal details, and emails them a confirmation. Membership and check-in history is kept
              (without personal details) so chargebacks can still be disputed.
            </div>
            <FormField style={{ marginBottom: 8 }}>
              <Label>Reason</Label>
              <Input
                placeholder="e.g. Requested by member via email"
                value={deleteForm.reason}
                onChange={(e) => setDeleteForm({ ...deleteForm, reason: e.target.value })}
              />
            </FormField>
            <FormField style={{ marginBottom: 8 }}>
              <Label>Type their email to confirm</Label>
              <Input
                placeholder={selectedUser.email}
                value={deleteForm.confirmEmail}
                onChange={(e) => setDeleteForm({ ...deleteForm, confirmEmail: e.target.value })}
              />
            </FormField>
            <Row>
              <Button
                variant="danger"
                onClick={handleDeleteAccount}
                disabled={
                  deleteLoading ||
                  !deleteForm.reason.trim() ||
                  deleteForm.confirmEmail.trim().toLowerCase() !== (selectedUser.email ?? "").toLowerCase()
                }
              >
                {deleteLoading ? "Deleting…" : "Delete account"}
              </Button>
            </Row>
            {deleteStatus && <StatusText success={deleteStatus.ok}>{deleteStatus.msg}</StatusText>}
          </Modal>
        </ModalOverlay>
      )}
    </>
  );
};

export default UsersPage;
